import { apiClient } from '../client';

export interface Bar {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  logoUrl: string | null;
  hours: Record<string, { open: string; close: string }> | null;
  address: string;
  latitude: number;
  longitude: number;
  websiteUrl: string | null;
  phoneNumber: string | null;
  timeZone: string | null;
  servesBrunch: boolean;
  outdoorSeating: boolean;
  allowsDogs: boolean;
  goodForWatchingSports: boolean;
  servesBeer: boolean;
  servesWine: boolean;
  servesCocktails: boolean;
  socialMedia: Record<string, string> | null;
  isClaimed: boolean;
  distance?: number;
  // Content mode fields
  contentMode?: 'auto' | 'manual' | 'hybrid';
  manualFields?: string[];
  aiGuidance?: string | null;
  specialsMode?: 'auto' | 'manual';
  eventsMode?: 'auto' | 'manual';
  // Parse config for crawl targets
  parseConfig?: {
    targets?: Array<{
      url: string;
      type: 'html' | 'pdf' | 'image';
      selector?: string;
      contentHash?: string;
    }>;
  } | null;
  // Timestamps
  lastCrawledAt?: string | null;
  lastExtractedAt?: string | null;
}

export interface Special {
  id: string;
  barId: string;
  type: 'drink' | 'food' | 'combo';
  name: string;
  description: string | null;
  price: string | null;
  rrule: string | null;
  isHappyHour: boolean;
  happyHourStart: string | null;
  happyHourEnd: string | null;
}

export interface Event {
  id: string;
  barId: string;
  name: string;
  description: string | null;
  startDatetime: string | null;
  endDatetime: string | null;
  isAllDay: boolean;
  rrule: string | null;
}

interface BarsListResponse {
  success: boolean;
  bars: Bar[];
  total: number;
  limit: number;
  offset: number;
}

interface BarDetailResponse {
  success: boolean;
  bar: Bar & { specials: Special[]; events: Event[] };
}

interface BarManageResponse {
  success: boolean;
  bar: Bar;
  role: 'admin' | 'owner' | 'manager';
}

interface ListBarsParams {
  lat?: number;
  lng?: number;
  radius?: number;
  limit?: number;
  offset?: number;
  search?: string;
}

export const barsApi = {
  list: async (params: ListBarsParams = {}): Promise<BarsListResponse> => {
    const { data } = await apiClient.get<BarsListResponse>('/v1/bars', {
      params,
    });
    return data;
  },

  /** Get bar by ID or slug */
  getById: async (idOrSlug: string): Promise<BarDetailResponse> => {
    const { data } = await apiClient.get<BarDetailResponse>(`/v1/bars/${idOrSlug}`);
    return data;
  },

  /** Get bar data for management (includes parse_config, timestamps, etc.) */
  getManage: async (id: string): Promise<BarManageResponse> => {
    const { data } = await apiClient.get<BarManageResponse>(`/v1/bars/${id}/manage`);
    return data;
  },
};
